import React, { useEffect, Suspense, lazy } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { DirectionProvider } from '@radix-ui/react-direction';
import { Loader2 } from 'lucide-react';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/context/AuthContext';
// Shell pieces are lazy so the landing page's critical bundle stays small
// (they pull radix menus/dialogs/sheet + the activation gate).
const ProtectedRoute = lazy(() => import('@/components/ProtectedRoute').then((m) => ({ default: m.ProtectedRoute })));
const AppShell = lazy(() => import('@/components/AppShell').then((m) => ({ default: m.AppShell })));
const SupportChatWidget = lazy(() => import('@/components/SupportChatWidget'));
import RouteTransitionBar from '@/components/RouteTransitionBar';

// Landing is the public entry point -> keep it eager so first paint is instant.
import Landing from '@/pages/Landing';

// Everything else is lazy-loaded so it is NOT part of the initial bundle.
// This is the main fix for the slow first load ("loads every page at once").
const Login = lazy(() => import('@/pages/Login'));
const Register = lazy(() => import('@/pages/Register'));
const GoogleCallback = lazy(() => import('@/pages/GoogleCallback'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const Models = lazy(() => import('@/pages/Models'));
const ModelDetail = lazy(() => import('@/pages/ModelDetail'));
const ApiKeys = lazy(() => import('@/pages/ApiKeys'));
const Usage = lazy(() => import('@/pages/Usage'));
const Billing = lazy(() => import('@/pages/Billing'));
const Playground = lazy(() => import('@/pages/Playground'));
const SetupGuidePage = lazy(() => import('@/pages/SetupGuidePage'));
const FeaturesPage = lazy(() => import('@/pages/FeaturesPage'));
const Blog = lazy(() => import('@/pages/Blog'));
const BlogPost = lazy(() => import('@/pages/BlogPost'));
const ComparePage = lazy(() => import('@/pages/ComparePage'));
const KeywordLanding = lazy(() => import('@/pages/KeywordLanding'));
const Support = lazy(() => import('@/pages/Support'));
const Referral = lazy(() => import('@/pages/Referral'));
const AdminDashboard = lazy(() => import('@/pages/admin/AdminDashboard'));
const AdminUsers = lazy(() => import('@/pages/admin/AdminUsers'));
const AdminDeposits = lazy(() => import('@/pages/admin/AdminDeposits'));
const AdminSupport = lazy(() => import('@/pages/admin/AdminSupport'));
const AdminModels = lazy(() => import('@/pages/admin/AdminModels'));
const AdminUsage = lazy(() => import('@/pages/admin/AdminUsage'));
const AdminTransactions = lazy(() => import('@/pages/admin/AdminTransactions'));
const AdminSettings = lazy(() => import('@/pages/admin/AdminSettings'));
const AdminActivations = lazy(() => import('@/pages/admin/AdminActivations'));
const AdminSecurity = lazy(() => import('@/pages/admin/AdminSecurity'));
const AdminBackup = lazy(() => import('@/pages/admin/AdminBackup'));
const AdminActivity = lazy(() => import('@/pages/admin/AdminActivity'));
const AdminBlog = lazy(() => import('@/pages/admin/AdminBlog'));
const AdminPromos = lazy(() => import('@/pages/admin/AdminPromos'));
const AdminChatbot = lazy(() => import('@/pages/admin/AdminChatbot'));
const AdminStudioPlans = lazy(() => import('@/pages/admin/AdminStudioPlans'));
const Verify = lazy(() => import('@/pages/Verify'));
const Terms = lazy(() => import('@/pages/Legal').then((m) => ({ default: m.Terms })));
const Privacy = lazy(() => import('@/pages/Legal').then((m) => ({ default: m.Privacy })));
const AUP = lazy(() => import('@/pages/Legal').then((m) => ({ default: m.AUP })));

// Smart legacy redirect: /verify → /app/verify if authed, else /register?next=/app/verify.
const VerifyLegacyRedirect = () => {
  const { user } = useAuth();
  return <Navigate to={user ? '/app/verify' : '/register?next=/app/verify'} replace />;
};

// Google OAuth callback: detect #session_id synchronously during render,
// BEFORE any route/ProtectedRoute logic runs (useEffect would be too late).
const GoogleAuthGate = ({ children }) => {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) return <GoogleCallback />;
  return children;
};

// Scroll to top on every route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname]);
  return null;
};

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background" data-testid="page-loader">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

const Shell = ({ children, admin = false }) => (
  <ProtectedRoute adminOnly={admin} allowGuest={!admin}>
    <AppShell>{children}</AppShell>
  </ProtectedRoute>
);

// Public informational pages (Features / Setup Guide / Models). These are open
// to everyone — including search-engine crawlers — so we auto-enable guest
// browsing for a seamless experience and better SEO (no login redirect).
const PublicShell = ({ children }) => {
  const { user, guest, loading, enterGuest } = useAuth();
  useEffect(() => {
    // Wait for the auth check to finish before deciding — otherwise logged-in
    // users (incl. admin) get flagged as guests during the me() round-trip and
    // see a "دخول / إنشاء حساب" flash before their session loads.
    if (loading) return;
    if (!user && !guest) enterGuest();
  }, [user, guest, loading, enterGuest]);
  return <AppShell>{children}</AppShell>;
};

function App() {
  return (
    <div className="App">
      <DirectionProvider dir="rtl">
        <AuthProvider>
          <BrowserRouter>
            <ScrollToTop />
            <RouteTransitionBar />
            <Suspense fallback={<PageLoader />}>
              <GoogleAuthGate>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                {/* Legacy /verify → moved behind auth as /app/verify */}
                <Route path="/verify" element={<VerifyLegacyRedirect />} />

                {/* Studio hidden (2026-09) — old links go home */}
                <Route path="/studio/*" element={<Navigate to="/" replace />} />

                {/* Public informational pages (SEO + guest) */}
                <Route path="/features" element={<PublicShell><FeaturesPage /></PublicShell>} />
                <Route path="/guide" element={<PublicShell><SetupGuidePage /></PublicShell>} />
                <Route path="/models" element={<PublicShell><Models /></PublicShell>} />
                <Route path="/models/:modelId" element={<PublicShell><ModelDetail /></PublicShell>} />
                <Route path="/blog" element={<PublicShell><Blog /></PublicShell>} />
                <Route path="/blog/:slug" element={<PublicShell><BlogPost /></PublicShell>} />
                <Route path="/compare/:competitor" element={<PublicShell><ComparePage /></PublicShell>} />
                {/* Transactional SEO landing pages (high-intent buying keywords) */}
                <Route path="/buy-ai-api-egypt" element={<PublicShell><KeywordLanding slug="buy-ai-api-egypt" /></PublicShell>} />
                <Route path="/ai-api-gateway" element={<PublicShell><KeywordLanding slug="ai-api-gateway" /></PublicShell>} />
                <Route path="/gpt-api-egypt" element={<PublicShell><KeywordLanding slug="gpt-api-egypt" /></PublicShell>} />
                <Route path="/claude-api-egypt" element={<PublicShell><KeywordLanding slug="claude-api-egypt" /></PublicShell>} />
                <Route path="/gemini-api-egypt" element={<PublicShell><KeywordLanding slug="gemini-api-egypt" /></PublicShell>} />
                <Route path="/ai-api-without-visa" element={<PublicShell><KeywordLanding slug="ai-api-without-visa" /></PublicShell>} />

                {/* Legal pages (public, no shell — self-contained layout) */}
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/aup" element={<AUP />} />
                {/* Legacy /refund removed — no refund policy. Redirect to Terms. */}
                <Route path="/refund" element={<Navigate to="/terms" replace />} />

                {/* User app */}
                <Route path="/app" element={<Shell><Dashboard /></Shell>} />
                <Route path="/app/models" element={<Navigate to="/models" replace />} />
                <Route path="/app/keys" element={<Shell><ApiKeys /></Shell>} />
                <Route path="/app/usage" element={<Shell><Usage /></Shell>} />
                <Route path="/app/billing" element={<Shell><Billing /></Shell>} />
                <Route path="/app/docs" element={<Navigate to="/guide" replace />} />
                <Route path="/app/guide" element={<Navigate to="/guide" replace />} />
                <Route path="/app/features" element={<Navigate to="/features" replace />} />
                <Route path="/app/playground" element={<Shell><Playground /></Shell>} />
                <Route path="/app/verify" element={<Shell><Verify /></Shell>} />
                <Route path="/app/referral" element={<Shell><Referral /></Shell>} />
                <Route path="/app/estimator" element={<Navigate to="/app/playground?tab=estimator" replace />} />
                <Route path="/app/support" element={<Shell><Support /></Shell>} />

                {/* Hidden admin panel (only reachable by admin accounts) */}
                <Route path="/admisoal" element={<Shell admin><AdminDashboard /></Shell>} />
                <Route path="/admisoal/users" element={<Shell admin><AdminUsers /></Shell>} />
                <Route path="/admisoal/activations" element={<Shell admin><AdminActivations /></Shell>} />
                <Route path="/admisoal/deposits" element={<Shell admin><AdminDeposits /></Shell>} />
                <Route path="/admisoal/promos" element={<Shell admin><AdminPromos /></Shell>} />
                <Route path="/admisoal/support" element={<Shell admin><AdminSupport /></Shell>} />
                <Route path="/admisoal/models" element={<Shell admin><AdminModels /></Shell>} />
                <Route path="/admisoal/usage" element={<Shell admin><AdminUsage /></Shell>} />
                <Route path="/admisoal/chatbot" element={<Shell admin><AdminChatbot /></Shell>} />
                <Route path="/admisoal/studio-plans" element={<Shell admin><AdminStudioPlans /></Shell>} />
                <Route path="/admisoal/activity" element={<Shell admin><AdminActivity /></Shell>} />
                <Route path="/admisoal/blog" element={<Shell admin><AdminBlog /></Shell>} />
                <Route path="/admisoal/transactions" element={<Shell admin><AdminTransactions /></Shell>} />
                <Route path="/admisoal/security" element={<Shell admin><AdminSecurity /></Shell>} />
                <Route path="/admisoal/backup" element={<Shell admin><AdminBackup /></Shell>} />
                <Route path="/admisoal/settings" element={<Shell admin><AdminSettings /></Shell>} />

                {/* Anything else -> home */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              </GoogleAuthGate>
            </Suspense>
            <Suspense fallback={null}>
              <SupportChatWidget />
            </Suspense>
          </BrowserRouter>
          <Toaster position="top-center" richColors closeButton />
        </AuthProvider>
      </DirectionProvider>
    </div>
  );
}

export default App;
